import { useEffect, useState } from "react";
import { DetalleDeTrabajos } from "./DetalleDeTrabajos";
import type { TrabajoDeLaAprobacion } from "./api";
import "./paneles.css";

/**
 * "Hoy": qué tengo encima ahora mismo.
 *
 * El orden de los bloques es el mensaje. Primero lo que espera una decisión
 * tuya, después lo que está corriendo, después lo terminado y al final el
 * estado del sistema. Un panel que empieza por gráficas de gasto obliga a
 * buscar lo importante.
 *
 * Rehecho el 22/10/2026 con `paneles.css`, como Tareas pendientes y Mando. Dos
 * cambios de fondo:
 *
 *   · Aquí ya no se aprueba. La aprobación se enseña entera —qué pasa, por qué,
 *     riesgo, cómo se vuelve atrás— y el botón lleva a Tareas pendientes, que
 *     es la única puerta (8.8: «una sola vía para aprobar»). Dos puertas
 *     acaban decidiendo distinto: ésta no miraba la lista de dentro.
 *   · Los fallos de hace más de 7 días se pliegan. Seguían arriba en «Te toca
 *     a ti» fallos de septiembre, y un aviso de hace siete semanas tapa el de
 *     esta mañana. No se borran: se ven con un clic.
 */

interface Hoy {
  teToca: {
    aprobaciones: {
      id: string;
      titulo: string;
      queVaAPasar: string;
      porQue: string;
      riesgo: string;
      explicacionDelRiesgo: string;
      comoSeVuelveAtras: string | null;
      recomendacion: string | null;
      trabajos?: TrabajoDeLaAprobacion[];
      origen?: { conversacionId: string; titulo: string } | null;
    }[];
    fallidas: { id: string; titulo: string; error: string | null; intentos: string; cuando: string }[];
    avisos: { jobId: string; textos: string[]; cuando: string }[];
  };
  enMarcha: {
    jobId: string;
    titulo: string;
    capacidad: string;
    agente: string | null;
    worker: string | null;
    segundos: number | null;
    paso: string | null;
    hechos: number | null;
    deCuantos: number | null;
    esperando: boolean;
    porQue: string | null;
  }[];
  terminadoHoy: {
    id: string;
    titulo: string;
    resumen: string;
    verificada: boolean;
    cuando: string;
    evidencias: { id: string; nombre: string; tipo: string }[];
  }[];
  sistema: {
    pausado: boolean;
    motivoDelFreno: string | null;
    vetadas: string[];
    ordenadores: { total: number; enLinea: number };
    gastoDelMesEur: number;
    presupuestoEur: number;
    proximaRenovacion: { cliente: string; servicio: string; dias: number } | null;
  };
}

function duracion(segundos: number | null): string {
  if (segundos === null) return "";
  if (segundos < 60) return `${segundos} s`;
  const m = Math.floor(segundos / 60);
  return m < 60 ? `${m} min ${segundos % 60} s` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

interface CambioHecho {
  donde: string;
  que: string;
  antes: string;
  despues: string;
}

interface HechoDelDia {
  id: string;
  titulo: string;
  bien: boolean;
  resumen: string;
  cuando: string | null;
  cambios: CambioHecho[];
  evidencias: { id: string; nombre: string; tipo: string }[];
}

/** El día de hoy como lo quiere <input type="date">. */
function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface Pendientes {
  urgente: { cuantas: number; lineas: string[] };
  cuandoSePueda: { cuantas: number; lineas: string[] };
}

const FECHA = new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "short", timeZone: "Europe/Madrid" });
const HOY_LARGO = new Intl.DateTimeFormat("es-ES", {
  weekday: "long",
  day: "numeric",
  month: "long",
  timeZone: "Europe/Madrid",
});
const eur = (n: number) => n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Lo de más de 7 días se pliega: el mismo plazo que «Atascado» en Mando. */
const SIETE_DIAS = 7 * 24 * 60 * 60 * 1000;

/** Lo que se le está preguntando a una fila, en la propia fila. */
type Pregunta = { tipo: "descartar-tarea" | "descartar-aviso" | "cancelar"; id: string } | { tipo: "parar" } | null;

export function HoyPanel({
  irA,
  dentroDelMando = false,
}: {
  irA?: (vista: "tareas") => void;
  /**
   * Pintado dentro de la parte «Hoy» del Mando (22/10/2026).
   *
   * Ahí ya hay cabecera, cifras, «Por decidir» y «Atascado», así que no se
   * repiten: se pinta sólo lo que el Mando no tenía — lo que corre ahora, lo
   * terminado con su tabla, los avisos para descartar, lo que hay por hacer y el
   * sistema con su freno.
   */
  dentroDelMando?: boolean;
}) {
  const [datos, setDatos] = useState<Hoy | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [pregunta, setPregunta] = useState<Pregunta>(null);
  const [motivoDelFreno, setMotivoDelFreno] = useState("parada manual");
  const [verViejos, setVerViejos] = useState(false);

  /*
   * El día que se está mirando en «Terminado». Hoy por defecto; con el
   * selector se viaja a cualquier otro — lo pidió Jorge para llevar el
   * control: ver qué se hizo, dónde, cómo estaba y cómo quedó.
   */
  const [dia, setDia] = useState<string>(hoyISO());
  const [hecho, setHecho] = useState<HechoDelDia[] | null>(null);

  async function cargarHecho(cual: string) {
    const r = await fetch(`/api/hoy/hecho?dia=${cual}`, {
      credentials: "same-origin",
    });
    if (r.ok) setHecho(((await r.json()) as { hecho: HechoDelDia[] }).hecho);
  }

  useEffect(() => {
    setHecho(null);
    void cargarHecho(dia);
    /* Sólo el día de hoy sigue moviéndose; el pasado no cambia. */
    if (dia !== hoyISO()) return;
    const t = setInterval(() => void cargarHecho(dia), 5_000);
    return () => clearInterval(t);
  }, [dia]);

  /*
   * Lo urgente y lo que puede esperar.
   *
   * Desde el 29/09 el parte sólo dice el número —«342 para cuando se
   * pueda»— y hasta hoy ese número no estaba en ninguna pantalla. Se carga
   * aparte y bajo demanda: son cientos de líneas y no hacen falta para
   * saber qué tienes encima ahora mismo.
   */
  const [porHacer, setPorHacer] = useState<Pendientes | null>(null);
  const [abierto, setAbierto] = useState(false);

  async function cargarPendientes() {
    const r = await fetch("/api/hoy/pendientes", { credentials: "same-origin" });
    if (r.ok) setPorHacer((await r.json()) as Pendientes);
  }

  async function cargar() {
    const r = await fetch("/api/hoy", { credentials: "same-origin" });
    if (r.ok) setDatos((await r.json()) as Hoy);
  }

  useEffect(() => {
    void cargar();
    /**
     * Cada cinco segundos, como el panel de ordenadores.
     *
     * Podría ir por SSE como el chat, pero para cuatro bloques que cambian
     * cada pocos minutos sería complejidad sin ganancia.
     */
    const t = setInterval(() => void cargar(), 5_000);
    return () => clearInterval(t);
  }, []);

  async function mandar(ruta: string, cuerpo?: Record<string, unknown>) {
    setOcupado(true);
    try {
      await fetch(ruta, {
        method: "POST",
        credentials: "same-origin",
        ...(cuerpo ? { headers: { "content-type": "application/json" }, body: JSON.stringify(cuerpo) } : {}),
      });
      setPregunta(null);
      await cargar();
    } finally {
      setOcupado(false);
    }
  }

  const freno = (parar: boolean) =>
    mandar("/api/hoy/freno", { parar, motivo: parar ? motivoDelFreno || "parada manual" : "reanudado desde el panel" });

  /** Los dos botones de «¿seguro?», dentro de la fila. */
  const confirmar = (texto: string, si: string, alDecirSi: () => void, peligro = false) => (
    <div className="hy-pregunta">
      <span className="q">{texto}</span>
      <button className="pnl-boton plano" onClick={() => setPregunta(null)}>
        Cancelar
      </button>
      <button className={`pnl-boton ${peligro ? "no" : "si"}`} disabled={ocupado} onClick={alDecirSi}>
        {si}
      </button>
    </div>
  );

  if (!datos)
    return dentroDelMando ? null : (
      <div className="pnl">
        <p className="pnl-sub">Cargando…</p>
      </div>
    );

  const { sistema, teToca, enMarcha } = datos;
  const reciente = (cuando: string) => Date.now() - new Date(cuando).getTime() < SIETE_DIAS;
  const fallidasNuevas = teToca.fallidas.filter((f) => reciente(f.cuando));
  const avisosNuevos = teToca.avisos.filter((a) => reciente(a.cuando));
  const viejos = teToca.fallidas.length - fallidasNuevas.length + teToca.avisos.length - avisosNuevos.length;
  const fallidas = verViejos ? teToca.fallidas : fallidasNuevas;
  const avisos = verViejos ? teToca.avisos : avisosNuevos;
  const pendientes = (dentroDelMando ? 0 : teToca.aprobaciones.length) + fallidasNuevas.length + avisosNuevos.length;
  const gastado = sistema.presupuestoEur > 0 ? sistema.gastoDelMesEur / sistema.presupuestoEur : 0;

  return (
    <div className={dentroDelMando ? "hy-dentro" : "pnl"}>
      {!dentroDelMando && (
      <header className="pnl-cabecera">
        <div>
          <h1>Hoy</h1>
          <p className="pnl-sub">
            <span className="hy-dia">{HOY_LARGO.format(new Date())}</span>
            {" · "}
            {sistema.pausado ? <span className="es-mal">Reparto parado</span> : "Reparto en marcha"}
            {" · "}
            {sistema.ordenadores.enLinea} de {sistema.ordenadores.total} ordenadores en línea
          </p>
        </div>
      </header>
      )}

      {sistema.pausado && (
        <div className="pnl-nota es-mal" role="alert">
          <p>
            <strong>Sistema parado.</strong>
            {sistema.motivoDelFreno ? ` ${sistema.motivoDelFreno}.` : ""} No sale trabajo nuevo hacia los
            ordenadores; lo que ya corría sigue.
          </p>
          <button className="pnl-boton si hy-reanudar" onClick={() => void freno(false)} disabled={ocupado}>
            Reanudar
          </button>
        </div>
      )}

      {!dentroDelMando && (
      <div className="pnl-cifras">
        <a className={`pnl-cifra es-${pendientes ? "aviso" : "ok"}`} href="#hy-te-toca">
          <span className="n">{pendientes}</span>
          <span className="l">te toca a ti</span>
        </a>
        <a className={`pnl-cifra es-${enMarcha.length ? "aviso" : "ok"}`} href="#hy-en-marcha">
          <span className="n">{enMarcha.length}</span>
          <span className="l">en marcha ahora</span>
        </a>
        <a className="pnl-cifra es-ok" href="#hy-terminado">
          <span className="n">{dia === hoyISO() ? (hecho?.length ?? "…") : "—"}</span>
          <span className="l">terminado hoy</span>
        </a>
        <a className={`pnl-cifra es-${gastado >= 0.9 ? "mal" : gastado >= 0.7 ? "aviso" : "ok"}`} href="#hy-sistema">
          <span className="n">
            {eur(sistema.gastoDelMesEur)}
            <span className="u">€</span>
          </span>
          <span className="l">gastado este mes, de {sistema.presupuestoEur} €</span>
        </a>
      </div>
      )}

      {(!dentroDelMando || fallidasNuevas.length + avisosNuevos.length + viejos > 0) && (
      <section className="pnl-seccion" id="hy-te-toca">
        <header>
          <h2>{dentroDelMando ? "Avisos para mirar o descartar" : "Te toca a ti"}</h2>
          <span className={`pnl-cuenta es-${pendientes ? "aviso" : "ok"}`}>{pendientes}</span>
        </header>

        {pendientes === 0 && !verViejos && (
          <p className="pnl-vacio">
            {dentroDelMando ? "Ningún aviso esta semana." : "Nada esperando una decisión tuya."}
          </p>
        )}

        {/**
         * La aprobación se enseña entera, no como "¿apruebas X?".
         *
         * Qué va a pasar, por qué, qué riesgo tiene y cómo se vuelve atrás.
         * Sin eso no es una decisión: es un botón que hay que pulsar para que
         * el sistema siga, y eso se acaba pulsando sin leer.
         */}
        {(dentroDelMando ? [] : teToca.aprobaciones).map((a) => (
          <article className="hy-aprobacion" key={a.id}>
            <h3>{a.titulo}</h3>
            <p className="hy-que-pasa">{a.queVaAPasar}</p>
            {a.trabajos && <DetalleDeTrabajos trabajos={a.trabajos} />}
            <dl className="hy-detalle">
              <div>
                <dt>Por qué</dt>
                <dd>{a.porQue}</dd>
              </div>
              <div>
                <dt>Riesgo</dt>
                <dd>{a.explicacionDelRiesgo}</dd>
              </div>
              {a.comoSeVuelveAtras && (
                <div>
                  <dt>Si sale mal</dt>
                  <dd>{a.comoSeVuelveAtras}</dd>
                </div>
              )}
              {a.recomendacion && (
                <div>
                  <dt>Recomiendo</dt>
                  <dd>{a.recomendacion}</dd>
                </div>
              )}
            </dl>
            <div className="hy-abajo">
              {/* De dónde salió: para leer el contexto antes de decidir. */}
              {a.origen && (
                <a className="pnl-boton plano" href={`/?conversacion=${encodeURIComponent(a.origen.conversacionId)}`}>
                  Viene de «{a.origen.titulo}»
                </a>
              )}
              <button className="pnl-boton si hy-ir" onClick={() => irA?.("tareas")}>
                Decidir en Tareas pendientes
              </button>
            </div>
          </article>
        ))}

        {/* Descartar no arregla nada y no lo disimula. Pero una lista de
            pendientes que no se puede vaciar se deja de mirar entera, y
            entonces lo urgente de verdad queda escondido entre restos. */}
        {fallidas.map((t) => (
          <div className="pnl-fila" key={t.id}>
            <span className={`pnl-punto es-${reciente(t.cuando) ? "mal" : "apagado"}`} />
            <div className="txt">
              <div className="t">{t.titulo}</div>
              <div className="s">
                Falló el {FECHA.format(new Date(t.cuando))}: {t.error ?? "sin motivo registrado"} · intento {t.intentos}
              </div>
            </div>
            {pregunta?.tipo === "descartar-tarea" && pregunta.id === t.id ? (
              confirmar("No se arregla nada: deja de avisarte.", "Quitar", () =>
                void mandar(`/api/hoy/descartar/${t.id}`, { tipo: "tarea" }),
              )
            ) : (
              <button
                className="pnl-boton"
                onClick={() => setPregunta({ tipo: "descartar-tarea", id: t.id })}
                disabled={ocupado}
              >
                Descartar
              </button>
            )}
          </div>
        ))}

        {avisos.map((a) => (
          <div className="pnl-fila" key={a.jobId}>
            <span className={`pnl-punto es-${reciente(a.cuando) ? "aviso" : "apagado"}`} />
            <div className="txt">
              <div className="t">Resultado con pegas</div>
              <div className="s">
                {FECHA.format(new Date(a.cuando))} · {a.textos.join(" ")}
              </div>
            </div>
            {pregunta?.tipo === "descartar-aviso" && pregunta.id === a.jobId ? (
              confirmar("No se arregla nada: deja de avisarte.", "Quitar", () =>
                void mandar(`/api/hoy/descartar/${a.jobId}`, { tipo: "aviso" }),
              )
            ) : (
              <button
                className="pnl-boton"
                onClick={() => setPregunta({ tipo: "descartar-aviso", id: a.jobId })}
                disabled={ocupado}
              >
                Descartar
              </button>
            )}
          </div>
        ))}

        {viejos > 0 && (
          <div className="hy-pliegue">
            <button className="pnl-boton plano" onClick={() => setVerViejos((v) => !v)}>
              {verViejos
                ? "Ocultar lo de hace más de 7 días"
                : `Ver ${viejos} de hace más de 7 días`}
            </button>
          </div>
        )}
      </section>
      )}

      <section className="pnl-seccion" id="hy-en-marcha">
        <header>
          <h2>En marcha ahora</h2>
          <span className="pnl-cuenta">{enMarcha.length}</span>
        </header>

        {enMarcha.length === 0 && <p className="pnl-vacio">Nada corriendo ni esperando.</p>}

        {enMarcha.map((t) => (
          <div className="pnl-fila" key={t.jobId}>
            {/* Lo que espera se distingue de lo que corre: sin esto, un
                trabajo retenido parecía uno que va lento. */}
            <span className={`pnl-punto es-${t.esperando ? "apagado" : "ok"} ${t.esperando ? "" : "hy-latido"}`} />
            <div className="txt">
              <div className="t">{t.titulo}</div>
              <div className="s">
                {[t.esperando ? "Esperando" : null, t.agente, t.worker, t.esperando ? null : duracion(t.segundos)]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {/* Y por qué espera, si se sabe: «en cola» a secas es lo que costó
                  una tarde el 24/09 y no se distingue de un trabajo averiado. */}
              {t.esperando && t.porQue && <div className="s">{t.porQue}</div>}
              {/* Por dónde va. Sin esto, un trabajo de veinte minutos es un
                  cronómetro y no se distingue de uno colgado. */}
              {t.paso && (
                <div className="hy-paso">
                  <span>
                    {t.deCuantos ? `${t.hechos ?? 0} de ${t.deCuantos} · ` : ""}
                    {t.paso}
                  </span>
                  {t.deCuantos ? (
                    <i>
                      <b style={{ width: `${Math.round(((t.hechos ?? 0) / t.deCuantos) * 100)}%` }} />
                    </i>
                  ) : null}
                </div>
              )}
            </div>
            {pregunta?.tipo === "cancelar" && pregunta.id === t.jobId ? (
              confirmar(
                "Si está corriendo, se para en la máquina.",
                "Cancelar el trabajo",
                () => void mandar(`/api/hoy/cancelar/${t.jobId}`),
                true,
              )
            ) : (
              <button
                className="pnl-boton"
                onClick={() => setPregunta({ tipo: "cancelar", id: t.jobId })}
                disabled={ocupado}
              >
                Cancelar
              </button>
            )}
          </div>
        ))}
      </section>

      <section className="pnl-seccion" id="hy-terminado">
        <header>
          <h2>Terminado</h2>
          {(hecho?.length ?? 0) > 0 && <span className="pnl-cuenta es-ok">{hecho!.length}</span>}
          <input
            className="hy-fecha ir"
            type="date"
            value={dia}
            max={hoyISO()}
            aria-label="Día"
            onChange={(e) => setDia(e.target.value || hoyISO())}
          />
        </header>

        {hecho === null && <p className="pnl-vacio">Cargando…</p>}
        {hecho !== null && hecho.length === 0 && (
          <p className="pnl-vacio">{dia === hoyISO() ? "Todavía nada hoy." : "Ese día no se terminó nada."}</p>
        )}

        {/*
         * Las preguntas de cada semana a ChatGPT, en UNA línea (26/10/2026): eran
         * sesenta filas iguales que tapaban todo lo demás. Se abren si se quiere.
         */}
        {(() => {
          const deChatgpt = (hecho ?? []).filter((t) => t.titulo.startsWith("Preguntar a ChatGPT"));
          if (deChatgpt.length === 0) return null;
          const mal = deChatgpt.filter((t) => !t.bien).length;
          return (
            <details className="pnl-fila hy-hecho hy-grupo">
              <summary>
                <span className={`pnl-punto es-${mal ? "aviso" : "ok"}`} />
                <span className="t">
                  ChatGPT de la semana: {deChatgpt.length} {deChatgpt.length === 1 ? "pregunta" : "preguntas"}
                  {mal > 0 && <span className="pnl-cuenta es-mal hy-junto">{mal} no salieron</span>}
                </span>
              </summary>
              <ul>
                {deChatgpt.map((t) => (
                  <li key={t.id}>
                    {t.titulo.replace("Preguntar a ChatGPT por ", "")}
                    {t.resumen ? ` · ${t.resumen}` : ""}
                    {!t.bien ? " · no salió" : ""}
                  </li>
                ))}
              </ul>
            </details>
          );
        })()}

        {(hecho ?? []).filter((t) => !t.titulo.startsWith("Preguntar a ChatGPT")).map((t) => (
          <div className="pnl-fila hy-hecho" key={t.id}>
            <span className={`pnl-punto es-${t.bien ? "ok" : "mal"}`} />
            <div className="txt">
              <div className="t">
                {t.titulo}
                {!t.bien && <span className="pnl-cuenta es-mal hy-junto">no salió</span>}
              </div>
              {t.resumen && <div className="s">{t.resumen}</div>}
              {/**
               * La tabla que pidió Jorge: dónde, qué campo, cómo estaba y cómo
               * está. Sale de la relectura de la web, no de la intención.
               */}
              {t.cambios.length > 0 && (
                <div className="hy-cambios">
                  <table>
                    <thead>
                      <tr>
                        <th>Dónde</th>
                        <th>Qué</th>
                        <th>Antes</th>
                        <th>Ahora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.cambios.map((c, i) => (
                        <tr key={i}>
                          <td className="donde">{c.donde}</td>
                          <td>{c.que}</td>
                          <td className="antes">{c.antes}</td>
                          <td className="despues">{c.despues}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* La evidencia se enlaza, no se describe: "1 captura" sin poder
                  abrirla sigue siendo la palabra del agente. */}
              {t.evidencias.length > 0 && (
                <div className="hy-pruebas">
                  {t.evidencias.map((e) => (
                    <a
                      key={e.id}
                      className="pnl-chip"
                      href={`/api/artefactos/${e.id}`}
                      target="_blank"
                      rel="noreferrer"
                      title={e.nombre}
                    >
                      {e.tipo} ↗
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </section>

      <section className="pnl-seccion">
        <header>
          <h2>Lo que hay por hacer</h2>
          <button
            className="pnl-boton ir"
            aria-expanded={abierto}
            onClick={() => {
              setAbierto(!abierto);
              if (!porHacer) void cargarPendientes();
            }}
          >
            {abierto ? "Ocultar" : "Ver"}
          </button>
        </header>
        {!abierto ? (
          <p className="pnl-vacio">Lo urgente y lo que puede esperar, por cliente. Es lo que el parte cuenta en una línea.</p>
        ) : !porHacer ? (
          <p className="pnl-vacio">Cargando…</p>
        ) : (
          <div className="hy-por-hacer">
            {(
              [
                ["Urgente", porHacer.urgente, "mal"],
                ["Cuando se pueda", porHacer.cuandoSePueda, ""],
              ] as const
            ).map(([nombre, bloque, tono]) => (
              <div key={nombre}>
                <h3>
                  {nombre} <span className={`pnl-cuenta ${tono ? `es-${tono}` : ""}`}>{bloque.cuantas}</span>
                </h3>
                <ul>
                  {bloque.lineas.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="pnl-seccion" id="hy-sistema">
        <header>
          <h2>El sistema</h2>
        </header>
        <div className="hy-sistema">
          <div>
            <span className="k">Reparto</span>
            <span className={`v ${sistema.pausado ? "es-mal" : "es-ok"}`}>{sistema.pausado ? "Parado" : "En marcha"}</span>
          </div>
          <div>
            <span className="k">Ordenadores</span>
            <span className="v">
              {sistema.ordenadores.enLinea} en línea
              {sistema.ordenadores.total !== sistema.ordenadores.enLinea && (
                <span className="de"> de {sistema.ordenadores.total}</span>
              )}
            </span>
          </div>
          <div>
            <span className="k">Gasto del mes</span>
            <span className="v">
              {eur(sistema.gastoDelMesEur)} € <span className="de">de {sistema.presupuestoEur} €</span>
            </span>
            <span className="hy-barra" aria-hidden="true">
              <b style={{ width: `${Math.min(100, Math.round(gastado * 100))}%` }} />
            </span>
          </div>
          <div>
            <span className="k">Próxima renovación</span>
            <span className="v">
              {sistema.proximaRenovacion ? `en ${sistema.proximaRenovacion.dias} días` : "ninguna cerca"}
            </span>
            {sistema.proximaRenovacion && (
              <span className="de">
                {sistema.proximaRenovacion.cliente} · {sistema.proximaRenovacion.servicio}
              </span>
            )}
          </div>
        </div>

        {!sistema.pausado && (
          <div className="hy-parar">
            {pregunta?.tipo === "parar" ? (
              <form
                className="hy-pregunta"
                onSubmit={(e) => {
                  e.preventDefault();
                  void freno(true);
                }}
              >
                {/* Por qué se para: queda registrado. */}
                <input
                  autoFocus
                  value={motivoDelFreno}
                  onChange={(e) => setMotivoDelFreno(e.target.value)}
                  aria-label="Por qué paras"
                  maxLength={200}
                />
                <button type="button" className="pnl-boton plano" onClick={() => setPregunta(null)}>
                  Cancelar
                </button>
                <button type="submit" className="pnl-boton no" disabled={ocupado}>
                  Parar el reparto
                </button>
              </form>
            ) : (
              <>
                <span className="pnl-sub">Deja de repartir trabajo. Lo que ya corre sigue, y se puede cancelar arriba.</span>
                <button className="pnl-boton no" onClick={() => setPregunta({ tipo: "parar" })} disabled={ocupado}>
                  Parar el sistema
                </button>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
